import React from 'react';

/**
 * Mobile track glyphs.
 *
 * The mobile `ds-*` track draws its icons as INLINE SVG on a 24 grid, never
 * Phosphor: the icon font is a desktop dependency and the mobile templates were
 * all authored with hand-drawn 24px paths. Phosphor stays on the `dsk-*` side,
 * behind `<Icon as={Glyph} />`.
 *
 * Every glyph is decorative here, because the control that holds it always
 * carries its own accessible name (a visible label in the tab bar, an
 * `aria-label` on an icon-only button). So they are all `aria-hidden`.
 *
 * `filled` is the mobile equivalent of Phosphor's fill weight and follows the
 * same doctrine: regular is resting, filled is ACTIVE. The active tab and a
 * cart holding items are filled. Never fill on hover.
 */

const base = {
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': 'true',
  focusable: 'false',
};

function svgProps(filled) {
  return filled ? { ...base, fill: 'currentColor', strokeWidth: 1 } : base;
}

export function GlyphStorefront({ filled = false }) {
  return (
    <svg {...svgProps(filled)}>
      <path d="M4 4h16l1 5a3 3 0 0 1-6 0 3 3 0 0 1-6 0 3 3 0 0 1-6 0Z" />
      <path d="M5 11v9h14v-9" fill="none" />
      <path d="M9.5 20v-5h5v5" fill="none" />
    </svg>
  );
}

export function GlyphSquares({ filled = false }) {
  return (
    <svg {...svgProps(filled)}>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.5" />
    </svg>
  );
}

export function GlyphHeart({ filled = false }) {
  return (
    <svg {...svgProps(filled)}>
      <path d="M12 20s-7.5-4.6-7.5-9.4A4.1 4.1 0 0 1 12 8a4.1 4.1 0 0 1 7.5 2.6C19.5 15.4 12 20 12 20Z" />
    </svg>
  );
}

export function GlyphBag({ filled = false }) {
  return (
    <svg {...svgProps(filled)}>
      <path d="M5 7h14l1 13H4L5 7Z" />
      <path d="M8.5 7V6a3.5 3.5 0 0 1 7 0v1" fill="none" />
    </svg>
  );
}

export function GlyphUser({ filled = false }) {
  return (
    <svg {...svgProps(filled)}>
      <circle cx="12" cy="8.5" r="3.75" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" fill="none" />
    </svg>
  );
}

/**
 * The dashboard glyph, and the one glyph in this set that cannot take the
 * shared fill treatment.
 *
 * It used to be `<circle r="8.5">` plus a 4px needle. Unfilled that is barely
 * a gauge; FILLED — which is what the active tab is — `fill: currentColor`
 * turned the circle into a solid disc and swallowed the needle inside it, so
 * the dashboard tab in the mobile tab bar rendered as a bare dot.
 *
 * A closed silhouette cannot express a dial: the whole point of the shape is
 * the space between the arc and the needle, and filling that space is the same
 * as deleting it. So this one draws its dial with `fill: none` at both weights
 * and says ACTIVE the other way the set already allows — a heavier stroke and
 * a solid pivot — rather than by flooding a shape that has no inside.
 */
export function GlyphGauge({ filled = false }) {
  return (
    <svg {...base} strokeWidth={filled ? 2.25 : base.strokeWidth}>
      {/* The dial: an arc open at the bottom, which is what makes it read as a
          gauge rather than as a circle with a line in it. */}
      <path d="M4 16.5a8.5 8.5 0 1 1 16 0" fill="none" />
      {/* The needle, long enough to cross most of the dial. */}
      <path d="M12 16.5l4.2-5.2" fill="none" />
      <circle cx="12" cy="16.5" r={filled ? 1.6 : 1.1} fill="currentColor" stroke="none" />
    </svg>
  );
}

export function GlyphChartLine({ filled = false }) {
  return (
    <svg {...svgProps(filled)}>
      <path d="M4 19V5" fill="none" />
      <path d="M4 19h16" fill="none" />
      <path d="M7.5 15l3.5-4 3 2.5L20 8" fill="none" />
    </svg>
  );
}

export function GlyphTag({ filled = false }) {
  return (
    <svg {...svgProps(filled)}>
      <path d="M4 11.5V4h7.5l8.5 8.5-7.5 7.5L4 11.5Z" />
      <circle cx="8.25" cy="8.25" r="1.25" fill="none" />
    </svg>
  );
}

export function GlyphGear({ filled = false }) {
  return (
    <svg {...svgProps(filled)}>
      <circle cx="12" cy="12" r="3.25" />
      <path
        d="M12 3.5l1.4 2.1 2.5-.4.6 2.5 2.3 1.1-1 2.3 1 2.3-2.3 1.1-.6 2.5-2.5-.4L12 20.5l-1.4-2.1-2.5.4-.6-2.5-2.3-1.1 1-2.3-1-2.3 2.3-1.1.6-2.5 2.5.4L12 3.5Z"
        fill="none"
      />
    </svg>
  );
}

export function GlyphList({ filled = false }) {
  return (
    <svg {...svgProps(filled)}>
      <path d="M4 6.5h16M4 12h16M4 17.5h16" fill="none" />
    </svg>
  );
}

export function GlyphArrowLeft({ filled = false }) {
  return (
    <svg {...svgProps(filled)}>
      <path d="M19 12H5" fill="none" />
      <path d="M11 6l-6 6 6 6" fill="none" />
    </svg>
  );
}

export function GlyphX({ filled = false }) {
  return (
    <svg {...svgProps(filled)}>
      <path d="M6 6l12 12M18 6L6 18" fill="none" />
    </svg>
  );
}

export function GlyphPlus({ filled = false }) {
  return (
    <svg {...svgProps(filled)}>
      <path d="M12 5v14M5 12h14" fill="none" />
    </svg>
  );
}

export function GlyphSun({ filled = false }) {
  return (
    <svg {...svgProps(filled)}>
      <circle cx="12" cy="12" r="4" />
      <path
        d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.2 5.2l1.4 1.4M17.4 17.4l1.4 1.4M18.8 5.2l-1.4 1.4M6.6 17.4l-1.4 1.4"
        fill="none"
      />
    </svg>
  );
}

export function GlyphMoon({ filled = false }) {
  return (
    <svg {...svgProps(filled)}>
      <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />
    </svg>
  );
}

export function GlyphSearch({ filled = false }) {
  return (
    <svg {...svgProps(filled)}>
      <circle cx="11" cy="11" r="6.5" fill="none" />
      <path d="M16 16l4 4" fill="none" />
    </svg>
  );
}

export function GlyphBell({ filled = false }) {
  return (
    <svg {...svgProps(filled)}>
      <path d="M6 9a6 6 0 0 1 12 0c0 4 1.5 5.5 1.5 5.5h-15S6 13 6 9Z" />
      <path d="M10 18a2 2 0 0 0 4 0" fill="none" />
    </svg>
  );
}

export function GlyphCheck({ filled = false }) {
  return (
    <svg {...svgProps(filled)}>
      <path d="M5 12.5l4.5 4.5L19 7.5" fill="none" />
    </svg>
  );
}

export function GlyphWarning({ filled = false }) {
  return (
    <svg {...svgProps(filled)}>
      <circle cx="12" cy="12" r="8.5" fill="none" />
      <path d="M12 7.5v5.5M12 16.2v.6" fill="none" />
    </svg>
  );
}

export function GlyphQuestion({ filled = false }) {
  return (
    <svg {...svgProps(filled)}>
      <circle cx="12" cy="12" r="8.5" fill="none" />
      <path d="M9.6 9.5a2.4 2.4 0 1 1 3.2 2.3c-.6.3-.8.8-.8 1.4v.3" fill="none" />
      <path d="M12 16.4v.4" fill="none" />
    </svg>
  );
}

/** The mobile tab bar and drawer address glyphs by id, so this is the map. */
export const GLYPHS = {
  storefront: GlyphStorefront,
  squares: GlyphSquares,
  heart: GlyphHeart,
  bag: GlyphBag,
  user: GlyphUser,
  gauge: GlyphGauge,
  chart: GlyphChartLine,
  tag: GlyphTag,
  gear: GlyphGear,
  list: GlyphList,
  arrowLeft: GlyphArrowLeft,
  x: GlyphX,
  plus: GlyphPlus,
  sun: GlyphSun,
  moon: GlyphMoon,
  search: GlyphSearch,
  bell: GlyphBell,
  check: GlyphCheck,
  warning: GlyphWarning,
  question: GlyphQuestion,
};

/**
 * @param {{ name: string, filled?: boolean }} props
 */
export function Glyph({ name, filled = false }) {
  const Component = GLYPHS[name];
  if (!Component) return null;
  return <Component filled={filled} />;
}

export default Glyph;
